<template>
  <div class="space-y-6">
    <!-- Basic Information -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border rounded-lg p-6'
      ]"
    >
      <h2
        class="text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700"
      >
        Basic Information
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <div>
          <label
            class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
          >
            Team Name
          </label>
          <input
            v-model="localTeam.name"
            type="text"
            class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label
            class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
          >
            Folder ID
          </label>
          <input
            v-model="localTeam.standupId"
            type="text"
            class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label
            class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
          >
            Roadmap Board ID
          </label>
          <input
            v-model="localTeam.roadmapBoardId"
            type="text"
            class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label
            class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
          >
            Parent Group
          </label>
          <input
            v-model="localTeam.parentGroup"
            type="text"
            class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label
            class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
          >
            Type
          </label>
          <input
            v-model="localTeam.type"
            type="text"
            class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          />
        </div>
      </div>
      <!-- Boolean Toggles -->
      <div class="flex flex-wrap gap-6 mt-4">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="localTeam.deleted"
            type="checkbox"
            class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
          />
          <span
            class="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400"
            >Deleted</span
          >
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="localTeam.isNew"
            type="checkbox"
            class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
          />
          <span
            class="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400"
            >Is New</span
          >
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="localTeam.sendToEmOnly"
            type="checkbox"
            class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
          />
          <span
            class="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400"
            >Send to EM Only</span
          >
        </label>
      </div>
    </div>

    <!-- Meta Information -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border rounded-lg p-6'
      ]"
    >
      <h2
        class="text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700"
      >
        Meta Information
      </h2>
      <div class="flex flex-wrap gap-6 mb-4">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="localTeam.meta!.isCombinedFolder"
            type="checkbox"
            class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
          />
          <span
            class="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400"
            >Is Combined Folder</span
          >
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="localTeam.meta!.skipPreviousQuarter"
            type="checkbox"
            class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
          />
          <span
            class="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400"
            >Skip Previous Quarter</span
          >
        </label>
      </div>
      <div>
        <label
          class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
        >
          Sub Modules (comma-separated)
        </label>
        <input
          :value="subModulesString"
          type="text"
          placeholder="Module1, Module2, Module3"
          class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          @input="updateSubModules(($event.target as HTMLInputElement).value)"
        />
      </div>
    </div>

    <!-- Managers -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border rounded-lg p-6'
      ]"
    >
      <h2
        class="text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700"
      >
        Managers
      </h2>

      <!-- Bulk Add -->
      <div
        class="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-dashed border-gray-300 dark:border-gray-600"
      >
        <label
          class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
        >
          Bulk Add Managers
        </label>
        <input
          v-model="bulkManagerEmails"
          type="text"
          placeholder="Paste comma-separated emails (e.g., email1@example.com, email2@example.com)"
          class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          @input="debouncedBulkLookup('manager')"
        />
        <!-- Bulk Results -->
        <div
          v-if="bulkManagerResults.length > 0"
          class="mt-3 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700"
        >
          <div class="text-xs font-semibold uppercase text-blue-600 dark:text-blue-400 mb-2">
            Found in Slack
          </div>
          <div class="space-y-1 max-h-40 overflow-y-auto mb-3">
            <div
              v-for="result in bulkManagerResults"
              :key="result.email"
              :class="[
                'text-sm p-2 rounded',
                result.found
                  ? 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              ]"
            >
              <strong>{{ result.email }}</strong>
              {{
                result.found
                  ? `→ ${result.emailPrefix}, ${result.slackId || 'No Slack ID'}`
                  : '→ Not found'
              }}
            </div>
          </div>
          <div class="flex gap-2">
            <button
              class="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm rounded font-medium"
              @click="applyBulkManagers"
            >
              Apply All
            </button>
            <button
              class="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded font-medium"
              @click="dismissBulkManagers"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>

      <!-- Manager List -->
      <div class="space-y-3">
        <div
          v-for="(manager, index) in localTeam.managers"
          :key="index"
          class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
        >
          <div class="flex flex-wrap gap-3 items-end">
            <div class="flex-1 min-w-[120px]">
              <label
                class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >Type</label
              >
              <input
                v-model="manager.type"
                type="text"
                placeholder="DIRECTOR, EM, TL, PM, GPM"
                class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>
            <div class="flex-[2] min-w-[200px] relative">
              <label
                class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >Email Prefix</label
              >
              <input
                v-model="manager.emailPrefix"
                type="text"
                placeholder="Enter email or prefix"
                class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                @input="debouncedEmailLookup(manager.emailPrefix, 'manager', index)"
              />
            </div>
            <div class="flex-1 min-w-[150px]">
              <label
                class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >Slack ID</label
              >
              <input
                v-model="manager.slackId"
                type="text"
                class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>
            <button
              class="px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded font-medium"
              @click="removeManager(index)"
            >
              Remove
            </button>
          </div>
        </div>
      </div>
      <button
        class="mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium text-sm"
        @click="addManager"
      >
        + Add Manager
      </button>
    </div>

    <!-- Developers -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border rounded-lg p-6'
      ]"
    >
      <h2
        class="text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700"
      >
        Developers
      </h2>

      <!-- Bulk Add -->
      <div
        class="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-dashed border-gray-300 dark:border-gray-600"
      >
        <label
          class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
        >
          Bulk Add Developers
        </label>
        <input
          v-model="bulkDevEmails"
          type="text"
          placeholder="Paste comma-separated emails (e.g., email1@example.com, email2@example.com)"
          class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          @input="debouncedBulkLookup('dev')"
        />
        <!-- Bulk Results -->
        <div
          v-if="bulkDevResults.length > 0"
          class="mt-3 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700"
        >
          <div class="text-xs font-semibold uppercase text-blue-600 dark:text-blue-400 mb-2">
            Found in Slack
          </div>
          <div class="space-y-1 max-h-40 overflow-y-auto mb-3">
            <div
              v-for="result in bulkDevResults"
              :key="result.email"
              :class="[
                'text-sm p-2 rounded',
                result.found
                  ? 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              ]"
            >
              <strong>{{ result.email }}</strong>
              {{
                result.found
                  ? `→ ${result.name || 'N/A'}, ${result.emailPrefix}, ${result.slackId || 'No Slack ID'}`
                  : '→ Not found'
              }}
            </div>
          </div>
          <div class="flex gap-2">
            <button
              class="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm rounded font-medium"
              @click="applyBulkDevelopers"
            >
              Apply All
            </button>
            <button
              class="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded font-medium"
              @click="dismissBulkDevelopers"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>

      <!-- Developer List -->
      <div class="space-y-3">
        <div
          v-for="(dev, index) in localTeam.devs"
          :key="index"
          class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
        >
          <div class="flex flex-wrap gap-3 items-end">
            <div class="flex-1 min-w-[150px]">
              <label
                class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >Name</label
              >
              <input
                v-model="dev.name"
                type="text"
                class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>
            <div class="flex-[2] min-w-[200px] relative">
              <label
                class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >Email Prefix</label
              >
              <input
                v-model="dev.emailPrefix"
                type="text"
                placeholder="Enter email or prefix"
                class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                @input="debouncedEmailLookup(dev.emailPrefix, 'dev', index)"
              />
            </div>
            <div class="flex-1 min-w-[150px]">
              <label
                class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >Slack ID</label
              >
              <input
                v-model="dev.slackId"
                type="text"
                class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>
            <button
              class="px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded font-medium"
              @click="removeDeveloper(index)"
            >
              Remove
            </button>
          </div>
        </div>
      </div>
      <button
        class="mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium text-sm"
        @click="addDeveloper"
      >
        + Add Developer
      </button>
    </div>

    <!-- Additional Information -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border rounded-lg p-6'
      ]"
    >
      <h2
        class="text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700"
      >
        Additional Information
      </h2>
      <div>
        <label
          class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
        >
          Ignore Devs (comma-separated email prefixes)
        </label>
        <input
          :value="ignoreDevsString"
          type="text"
          placeholder="prefix1, prefix2, prefix3"
          class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          @input="updateIgnoreDevs(($event.target as HTMLInputElement).value)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue'
import { useTheme } from '../../../utils/useTheme'
import { taskSentinelService } from '../../../services/task-sentinel'
import type { Team, Manager, Developer, BulkEmailResult } from '../../../types/task-sentinel'

const theme = inject('theme', useTheme())

const props = defineProps<{
  team: Team | null
}>()

const emit = defineEmits<{
  (e: 'update:team', value: Team): void
  (e: 'lookup-email', email: string, type: 'manager' | 'dev', index: number): void
}>()

// Local copy of team for editing
const localTeam = ref<Team>({
  id: '',
  name: '',
  deleted: false,
  managers: [],
  devs: [],
  meta: {
    isCombinedFolder: false,
    skipPreviousQuarter: false,
    subModules: []
  }
})

// Bulk add state
const bulkManagerEmails = ref('')
const bulkDevEmails = ref('')
const bulkManagerResults = ref<BulkEmailResult[]>([])
const bulkDevResults = ref<BulkEmailResult[]>([])

// Debounce timers
let emailLookupTimer: ReturnType<typeof setTimeout> | null = null
let bulkLookupTimer: ReturnType<typeof setTimeout> | null = null

// Computed
const subModulesString = computed(() => {
  return localTeam.value.meta?.subModules?.join(', ') || ''
})

const ignoreDevsString = computed(() => {
  return localTeam.value.ignoreDevs?.join(', ') || ''
})

// Flag to prevent circular updates
let isUpdatingFromProp = false

// Watch for prop changes - only update if significantly different
watch(
  () => props.team,
  (newTeam) => {
    if (newTeam && !isUpdatingFromProp) {
      const newTeamStr = JSON.stringify(newTeam)
      const localTeamStr = JSON.stringify(localTeam.value)

      // Only update if actually different
      if (newTeamStr !== localTeamStr) {
        isUpdatingFromProp = true
        localTeam.value = JSON.parse(newTeamStr)
        // Ensure meta exists
        if (!localTeam.value.meta) {
          localTeam.value.meta = {
            isCombinedFolder: false,
            skipPreviousQuarter: false,
            subModules: []
          }
        }
        // Reset flag after next tick
        setTimeout(() => {
          isUpdatingFromProp = false
        }, 0)
      }
    }
  },
  { immediate: true, deep: true }
)

// Watch for local changes and emit - skip if update came from prop
watch(
  localTeam,
  (newValue) => {
    if (!isUpdatingFromProp) {
      emit('update:team', JSON.parse(JSON.stringify(newValue)))
    }
  },
  { deep: true }
)

// Methods
const updateSubModules = (value: string) => {
  localTeam.value.meta!.subModules = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s)
}

const updateIgnoreDevs = (value: string) => {
  localTeam.value.ignoreDevs = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s)
}

const addManager = () => {
  localTeam.value.managers.push({
    type: '',
    emailPrefix: '',
    slackId: ''
  })
}

const removeManager = (index: number) => {
  localTeam.value.managers.splice(index, 1)
}

const addDeveloper = () => {
  localTeam.value.devs.push({
    name: '',
    emailPrefix: '',
    slackId: ''
  })
}

const removeDeveloper = (index: number) => {
  localTeam.value.devs.splice(index, 1)
}

const debouncedEmailLookup = (value: string, type: 'manager' | 'dev', index: number) => {
  if (emailLookupTimer) {
    clearTimeout(emailLookupTimer)
  }

  if (value.includes('@')) {
    emailLookupTimer = setTimeout(() => {
      emit('lookup-email', value, type, index)
    }, 800)
  }
}

const debouncedBulkLookup = (type: 'manager' | 'dev') => {
  if (bulkLookupTimer) {
    clearTimeout(bulkLookupTimer)
  }

  const emailsText = type === 'manager' ? bulkManagerEmails.value : bulkDevEmails.value
  const emails = emailsText
    .split(',')
    .map((e) => e.trim())
    .filter((e) => e && e.includes('@'))

  if (emails.length === 0) {
    if (type === 'manager') {
      bulkManagerResults.value = []
    } else {
      bulkDevResults.value = []
    }
    return
  }

  bulkLookupTimer = setTimeout(async () => {
    const results: BulkEmailResult[] = []

    for (const email of emails) {
      try {
        const response = await taskSentinelService.lookupUserByEmail(email)
        if (response.success && response.data) {
          results.push({
            email,
            emailPrefix: taskSentinelService.extractEmailPrefix(response.data.email),
            slackId: response.data.slackId || '',
            name: response.data.name,
            found: true
          })
        } else {
          results.push({
            email,
            emailPrefix: '',
            slackId: '',
            found: false
          })
        }
      } catch {
        results.push({
          email,
          emailPrefix: '',
          slackId: '',
          found: false
        })
      }
    }

    if (type === 'manager') {
      bulkManagerResults.value = results
    } else {
      bulkDevResults.value = results
    }
  }, 1000)
}

const applyBulkManagers = () => {
  const foundResults = bulkManagerResults.value.filter((r) => r.found)
  for (const result of foundResults) {
    localTeam.value.managers.push({
      type: '',
      emailPrefix: result.emailPrefix,
      slackId: result.slackId
    })
  }
  bulkManagerEmails.value = ''
  bulkManagerResults.value = []
}

const dismissBulkManagers = () => {
  bulkManagerEmails.value = ''
  bulkManagerResults.value = []
}

const applyBulkDevelopers = () => {
  const foundResults = bulkDevResults.value.filter((r) => r.found)
  for (const result of foundResults) {
    localTeam.value.devs.push({
      name: result.name || '',
      emailPrefix: result.emailPrefix,
      slackId: result.slackId
    })
  }
  bulkDevEmails.value = ''
  bulkDevResults.value = []
}

const dismissBulkDevelopers = () => {
  bulkDevEmails.value = ''
  bulkDevResults.value = []
}
</script>
